// What a workflow is given when it is started now or on a schedule: the
// Run dialog's fields for the workflow's own parameters, kept as the
// schedule's `what.params` (or a run's `params`). A workflow with none
// shows nothing; one that could not be read says so (a saved row's values
// are left as they are). Each change also says whether the
// form flags a value (a required one emptied, a half-typed number): those
// are left out of the params, so a start now must wait for them.

import { useState } from "react";

import { useWorkflow } from "@/api/workflows";
import { Skeleton } from "@/components/ui/Skeleton";
import { ParamFields, useSourceOptions, type SourceOption } from "@/features/workflows/editor/ParamFields";
import { scheduleForm, scheduleFormProblems, scheduleParams } from "@/features/workflows/editor/scheduleModel";
import type { RunForm, RunFormValue } from "@/features/workflows/model";

interface WorkflowParamsFieldsProps {
  customerId: string;
  slug: string;
  params: Record<string, unknown>;
  showProblems: boolean;
  onChange: (params: Record<string, unknown>, bad: boolean) => void;
}

export function WorkflowParamsFields({ customerId, slug, params, showProblems, onChange }: WorkflowParamsFieldsProps) {
  const detail = useWorkflow(customerId || null, slug || null);
  if (!slug) return <p className="text-sm text-fg-muted">Choose the workflow first.</p>;
  if (detail.isLoading) return <Skeleton className="h-16 w-full rounded-card" aria-label="Loading the workflow" />;
  const spec = detail.data?.spec;
  if (!spec) return <p className="text-sm text-fg-muted">The workflow could not be read.</p>;
  const specParams = spec.parameters ?? [];
  if (specParams.length === 0) return null;
  return <ParamsForm key={slug} customerId={customerId} spec={spec} params={params} showProblems={showProblems} onChange={onChange} />;
}

type Spec = NonNullable<ReturnType<typeof useWorkflow>["data"]>["spec"];

function ParamsForm({ customerId, spec, params, showProblems, onChange }: { customerId: string; spec: Spec; params: Record<string, unknown>; showProblems: boolean; onChange: (params: Record<string, unknown>, bad: boolean) => void }) {
  const specParams = spec.parameters ?? [];
  const [form, setForm] = useState<RunForm>(() => scheduleForm(specParams, { enabled: true, cron: "", tz: "", params }));
  const sources: SourceOption[] = useSourceOptions(customerId);
  const problems = scheduleFormProblems(specParams, form);
  const set = (key: string, v: RunFormValue) => {
    const next = { ...form, [key]: v };
    setForm(next);
    onChange(scheduleParams(specParams, next), Object.keys(scheduleFormProblems(specParams, next)).length > 0);
  };
  return <ParamFields params={specParams} form={form} onChange={set} problems={problems} showProblems={showProblems} sources={sources} />;
}
